import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  FileText, Upload, CheckCircle, XCircle, AlertCircle, 
  Eye, Trash2, Download 
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration des types de documents
const DOCUMENT_CATEGORIES = {
  vehicule: {
    label: '🚗 Documents du véhicule',
    docs: [
      { type: 'carte_grise', label: 'Carte grise' },
      { type: 'assurance_camion', label: 'Assurance' },
      { type: 'visite_technique', label: 'Visite technique' },
    ]
  },
  chauffeur: {
    label: '👤 Documents du chauffeur',
    docs: [
      { type: 'cni_chauffeur', label: 'CNI' },
      { type: 'permis_conduire', label: 'Permis de conduire' },
    ]
  },
  bons: {
    label: '📦 Bons & photos de l\'opération',
    docs: [
      { type: 'bon_chargement', label: 'Bon de chargement' },
      { type: 'bon_livraison', label: 'Bon de livraison' },
      { type: 'photos_chargement', label: 'Photos du chargement' },
      { type: 'photos_livraison', label: 'Photos de la livraison' },
      { type: 'photos_depart', label: 'Photos avant départ' },
      { type: 'autre', label: 'Autre document' },
    ]
  },
};

const DocumentsChecklist = ({ operationId, userRole }) => {
  const [documents, setDocuments] = useState([]);
  const [missingDocs, setMissingDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDocType, setSelectedDocType] = useState(null);

  const isPrivileged = ['admin', 'operateur', 'commercial', 'field_ops'].includes(userRole);

  useEffect(() => {
    fetchDocuments();
    checkMissingDocuments();
  }, [operationId]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API}/operations/${operationId}/documents`);
      setDocuments(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les documents",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const checkMissingDocuments = async () => {
    try {
      const response = await axios.get(`${API}/operations/${operationId}/documents/missing`);
      setMissingDocs(response.data.missing_documents || []);
    } catch (error) {
      console.error('Error checking missing documents:', error);
    }
  };

  const handleFileSelect = (docType, event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setSelectedDocType(docType);
      handleUpload(docType, file);
    }
  };

  const handleUpload = async (docType, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('documentType', docType);

      await axios.post(
        `${API}/operations/${operationId}/documents`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      toast({
        title: "✅ Document ajouté",
        description: `Le document a été uploadé avec succès`
      });

      fetchDocuments();
      checkMissingDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      const errorMsg = error.response?.data?.detail || "Impossible d'uploader le document";
      toast({
        title: "Erreur",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      setSelectedFile(null);
      setSelectedDocType(null);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

    try {
      await axios.delete(`${API}/operations/${operationId}/documents/${docId}`);
      toast({
        title: "✅ Document supprimé",
        description: "Le document a été supprimé avec succès"
      });
      fetchDocuments();
      checkMissingDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le document",
        variant: "destructive"
      });
    }
  };

  const getDocumentStatus = (docType) => {
    const docs = documents.filter(d => d.documentType === docType);
    if (docs.length === 0) return 'missing';
    
    const latestDoc = docs[0];
    if (latestDoc.status === 'validated') return 'validated';
    if (latestDoc.status === 'rejected') return 'rejected';
    return 'pending';
  };

  const getDocuments = (docType) => {
    return documents.filter(d => d.documentType === docType);
  };

  const renderStatusIcon = (status) => {
    switch (status) {
      case 'validated':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'pending':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-400" />;
    }
  };

  const renderStatusBadge = (status) => {
    const config = {
      validated: { label: 'Validé', className: 'bg-green-100 text-green-800' },
      rejected: { label: 'Rejeté', className: 'bg-red-100 text-red-800' },
      pending: { label: 'En attente', className: 'bg-yellow-100 text-yellow-800' },
      missing: { label: 'Manquant', className: 'bg-gray-100 text-gray-800' }
    };
    const statusConfig = config[status] || config.missing;
    return <Badge className={`${statusConfig.className} border-0`}>{statusConfig.label}</Badge>;
  };

  if (loading) {
    return <div className="text-center py-8">Chargement des documents...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Alerte documents manquants — admin/staff uniquement */}
      {isPrivileged && missingDocs.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-orange-900">Documents manquants</h4>
                <p className="text-sm text-orange-700 mt-1">
                  Les documents suivants sont requis pour continuer : {missingDocs.map(d => d.label).join(', ')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des documents par catégorie */}
      {Object.entries(DOCUMENT_CATEGORIES).map(([categoryKey, category]) => {
        // Côté client : ne montrer que les docs présents, masquer la catégorie si vide
        const visibleDocs = isPrivileged
          ? category.docs
          : category.docs.filter((d) => getDocuments(d.type).length > 0);
        if (visibleDocs.length === 0) return null;
        return (
        <Card key={categoryKey}>
          <CardHeader>
            <CardTitle className="text-lg">{category.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {visibleDocs.map((doc) => {
                const status = getDocumentStatus(doc.type);
                const docs = getDocuments(doc.type);

                return (
                  <div
                    key={doc.type}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {renderStatusIcon(status)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{doc.label}</span>
                          {renderStatusBadge(status)}
                          {docs.length > 1 && (
                            <span className="text-xs text-gray-500">({docs.length} versions)</span>
                          )}
                        </div>
                        {docs.length > 0 && (
                          <p className="text-sm text-gray-500 mt-1">
                            Dernier upload: {new Date(docs[0].uploadedAt).toLocaleDateString('fr-FR')} 
                            {' par '} {docs[0].uploadedByName}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {docs.length > 0 && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`${BACKEND_URL}${docs[0].filePath}`, '_blank')}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Voir
                          </Button>
                          <a
                            href={`${BACKEND_URL}${docs[0].filePath}`}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button
                              variant="outline"
                              size="sm"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Télécharger
                            </Button>
                          </a>
                          {isPrivileged && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDelete(docs[0].id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </>
                      )}

                      {/* Upload button */}
                      {isPrivileged && (
                        <label>
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => handleFileSelect(doc.type, e)}
                            disabled={uploading}
                            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          />
                          <Button
                            variant={status === 'missing' ? 'default' : 'outline'}
                            size="sm"
                            disabled={uploading}
                            as="span"
                            className="cursor-pointer"
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            {docs.length > 0 ? 'Remplacer' : 'Ajouter'}
                          </Button>
                        </label>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
};

export default DocumentsChecklist;
